import { CurrencyPipe } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { EventTrackingService } from '../../../../services/event-tracking.service';
import {
  ProfileResidenceResponse,
  ResidenceOwnershipLabel,
  ResidenceOwnershipValue,
} from '../../models/profile-residence.model';
import {
  selectProfileResidenceFailure,
  selectProfileResidenceProcessed,
  selectProfileResidenceProcessing,
  selectProfileResidenceResponse,
} from '../../store/selectors/profile.selectors';
import { ProfileModalData } from '../../models/profile-details.model';

@Component({
    selector: 'app-profile-residence',
    templateUrl: './profile-residence.component.html',
    styleUrls: ['./profile-residence.component.scss'],
    standalone: false
})
export class ProfileResidenceComponent implements OnInit, OnDestroy {
  isEditMode = false;
  residenceData: ProfileResidenceResponse;
  modalData: ProfileModalData;
  unsubscribe$ = new Subject();
  entries: any = [];
  ownershipStatuses = [
    { label: ResidenceOwnershipLabel.RESIDENCE_RENT, value: ResidenceOwnershipValue.RESIDENCE_RENT },
    { label: ResidenceOwnershipLabel.RESIDENCE_PAY_MORTGAGE, value: ResidenceOwnershipValue.RESIDENCE_PAY_MORTGAGE },
    { label: ResidenceOwnershipLabel.RESIDENCE_NO_MORTGAGE, value: ResidenceOwnershipValue.RESIDENCE_NO_MORTGAGE },
    { label: ResidenceOwnershipLabel.RESIDENCE_OTHER, value: ResidenceOwnershipValue.RESIDENCE_OTHER },
  ];
  residenceForm: FormGroup;
  isLoading$ = this.store.select(selectProfileResidenceProcessing);
  hasError$ = this.store.select(selectProfileResidenceFailure);
  isProcessed$ = this.store.select(selectProfileResidenceProcessed);

  constructor(
    private readonly store: Store,
    private readonly currencyPipe: CurrencyPipe,
    private readonly eventTrackingService: EventTrackingService
  ) {}

  sendPageViewEvent() {
    const eventData = {
      eventName: 'pageView',
      // eventRepo: 'auth',
    };
    this.eventTrackingService.trackPageViewEvent(eventData);
  }

  ngOnInit(): void {
    this.sendPageViewEvent();
    this.store
      .select(selectProfileResidenceFailure)
      .pipe(
        filter((val) => !!val),
        takeUntil(this.unsubscribe$)
      )
      .subscribe((error) => {
        this.entries = this.getResidenceEntries(null);
        this.residenceForm = this.buildForm();
      });

    this.store
      .select(selectProfileResidenceResponse)
      .pipe(
        filter((val) => !!val),
        takeUntil(this.unsubscribe$)
      )
      .subscribe((residenceData) => {
        this.residenceForm = this.buildForm(residenceData[0]);

        if (residenceData.length > 0) {
          this.isEditMode = false;
          this.residenceData = residenceData[0];
          this.entries = this.getResidenceEntries(this.residenceData);
          this.modalData = { ...this.residenceData, profileType: 'residence' };
        } else {
          this.isEditMode = true;
          this.residenceData = null;
        }
      });
  }

  getResidenceEntries(residenceData: ProfileResidenceResponse) {
    return [
      {
        label: 'Ownership Status',
        value: residenceData?.ownershipStatusDescription,
      },
      {
        label: 'Monthly Payment',
        value: this.currencyPipe.transform(residenceData?.monthlyPayment, 'USD'),
      },
    ];
  }

  buildForm(data: ProfileResidenceResponse = null) {
    return new FormGroup({
      ownershipStatusId: new FormControl(
        this.ownershipStatuses.find((status) => status.value === data?.ownershipStatusId)
      ),
      monthlyPayment: new FormControl(data?.monthlyPayment),
    });
  }

  toggleIsEditMode() {
    this.isEditMode = !this.isEditMode;

    if (!this.isEditMode) {
      // scroll back to top when switched out of edit mode
      window.scrollTo(0, 0);
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }
}
